import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Mail } from 'lucide-react';
import { api } from '@/api/client';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/account/email')({ component: ChangeEmailPage });

const schema = z.object({ newEmail: z.string().email('Enter a valid email address') });
type Values = z.infer<typeof schema>;

function ChangeEmailPage() {
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { newEmail: '' },
  });
  const submit = useMutation({
    mutationFn: (v: Values) => api('/account/change-email-request', { method: 'POST', json: v }),
  });

  return (
    <div className="space-y-6">
      <div>
        <div className="section-ref">Email</div>
        <h2 className="mt-1.5 font-serif text-2xl font-medium tracking-tight">Change your email</h2>
        <div className="mt-1 gov-rule" aria-hidden />
        <p className="mt-4 max-w-prose text-[14px] leading-relaxed text-(--color-ink-2)">
          We'll send a confirmation link to your new address. Your existing email keeps working
          until you click the link — no risk of being locked out.
        </p>
      </div>

      <Card>
        <CardContent className="p-7">
          <form onSubmit={form.handleSubmit((v) => submit.mutate(v))} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email-new" required>
                New email address
              </Label>
              <Input
                id="email-new"
                type="email"
                autoComplete="email"
                placeholder="you@example.com.au"
                aria-invalid={!!form.formState.errors.newEmail || undefined}
                {...form.register('newEmail')}
              />
              {form.formState.errors.newEmail && (
                <p className="flex items-center gap-1.5 text-[12px] font-medium text-(--color-maroon)">
                  <AlertTriangle className="size-3.5" />
                  {form.formState.errors.newEmail.message}
                </p>
              )}
            </div>
            {submit.isSuccess && (
              <p className="flex items-start gap-2.5 rounded-(--radius-sm) border-l-[3px] border-(--color-forest) bg-(--color-forest-soft) px-3 py-2.5 text-[13px] font-medium text-(--color-forest)">
                <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
                Confirmation link sent. Check the new inbox to complete the change.
              </p>
            )}
            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={submit.isPending}>
                <Mail className="size-4" />
                {submit.isPending ? 'Sending…' : 'Send confirmation link'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
